import React, { type FunctionComponent, Fragment } from 'react'
import { useParams } from 'react-router'
import { css } from '@emotion/react'
import { spacing } from '@retailer-platform/shared-components'
import { PageHeader } from '@retailer-platform/shared-components'
import { BannerBreadcrumbs } from 'domains/banners/components/banner-breadcrumbs/BannerBreadcrumbs'
import { BannerDetailsNotFound } from 'domains/banners/components/banner-details/components/BannerDetailsNotFound'
import { BannerDetailsNotSupported } from 'domains/banners/components/banner-details/components/BannerDetailsNotSupported'
import { BannerDetailsContentText } from 'domains/banners/components/banner-details/components/BannerDetailsContentText'
import { FlyoutDetailsContentText } from 'domains/banners/components/banner-details/components/FlyoutDetailsContentText'
import { BannerDetailsSecondaryNav } from 'domains/banners/components/banner-details/components/BannerDetailsSecondaryNav'
import { BannerDetailsDetail } from 'domains/banners/components/banner-details/components/BannerDetailsDetail'
import { BannerDetailsPlatform } from 'domains/banners/components/banner-details/components/BannerDetailsPlatform'
import { BannerDetailsLinkDestination } from 'domains/banners/components/banner-details/components/BannerDetailsLinkDestination'
import { BannerDetailsContentImage } from 'domains/banners/components/banner-details/components/BannerDetailsContentImage'
import { BannerDetailsExperiment } from 'domains/banners/components/banner-details/components/BannerDetailsExperiment'
import { useQueryBanner } from 'domains/banners/utils/useQueryBanner.hooks'
import { bannerTypes } from 'domains/banners/constants/banner.constants'
import {
  type BannerQueryParams,
  type ImageWithNullableDesktop,
} from 'domains/banners/types/banner.types'
import { LoadingPage } from 'gin-and-tonic/containers/loading-page/LoadingPage'
import { ErrorPage } from 'gin-and-tonic/containers/error-page/ErrorPage'
import {
  type ContentManagementFlyoutPlacement,
  type ContentManagementHeroBannerPlacement,
  type ContentManagementSecondaryBannerPlacement,
} from 'domains/banners/api'
import { isHeroBanner, isSecondaryBanner, isFlyout } from 'domains/banners/utils/bannerUtils'

const cardsContainer = css({
  margin: '0 auto',
  maxWidth: '960px',
  paddingTop: spacing.X32,
  paddingRight: spacing.X20,
  paddingBottom: spacing.X32,
  paddingLeft: spacing.X20,
  width: '100%',
})

export const BannerDetails: FunctionComponent = () => {
  const { placementId } = useParams<BannerQueryParams>()

  const {
    apiResult: unknownApiResult,
    apiError,
    apiLoading,
  } = useQueryBanner({
    placementId: placementId,
  })

  if (apiError) return <ErrorPage action={() => null} />
  if (apiLoading) return <LoadingPage />
  if (unknownApiResult == undefined) return <BannerDetailsNotFound />
  if (unknownApiResult.type == bannerTypes.UNKNOWN) return <BannerDetailsNotSupported />

  const apiResult = unknownApiResult as
    | ContentManagementFlyoutPlacement
    | ContentManagementHeroBannerPlacement
    | ContentManagementSecondaryBannerPlacement

  const isBanner = isHeroBanner(apiResult.type) || isSecondaryBanner(apiResult.type)

  const flyoutResult = unknownApiResult as unknown as ContentManagementFlyoutPlacement

  const heroResult = unknownApiResult as ContentManagementHeroBannerPlacement
  const secondaryResult = unknownApiResult as ContentManagementSecondaryBannerPlacement
  const bannerResult = unknownApiResult as
    | ContentManagementHeroBannerPlacement
    | ContentManagementSecondaryBannerPlacement
  const images = apiResult.images as ImageWithNullableDesktop

  return (
    <Fragment>
      <PageHeader
        addBottomPadding
        title={<BannerBreadcrumbs pageType="details" placementName={apiResult.name} />}
      />
      <BannerDetailsSecondaryNav placementName={apiResult.name} />
      <div css={cardsContainer}>
        <BannerDetailsDetail
          scheduling={apiResult.scheduling}
          name={apiResult.name}
          status={apiResult.status}
          rank={apiResult.rank}
          id={apiResult.id}
          bannerId={apiResult.bannerId}
          enabled={apiResult.enabled}
        />
        <BannerDetailsPlatform placementPlatform={apiResult.platform} />

        <BannerDetailsExperiment experiment={apiResult.experiment} />

        {isBanner && <BannerDetailsLinkDestination destination={bannerResult.destination} />}

        <BannerDetailsContentImage
          mobileUrl={images?.mobile}
          desktopUrl={images?.nullableDesktop}
        />

        {isFlyout(apiResult.type) && (
          <FlyoutDetailsContentText
            titleText={flyoutResult.titleText}
            descriptionText={flyoutResult.descriptionText}
            disclaimerText={flyoutResult.disclaimerText}
            primaryButton={flyoutResult.primaryButton}
            secondaryButton={flyoutResult.secondaryButton}
            linkButton={flyoutResult.linkButton}
            primaryDestination={flyoutResult.primaryDestination}
            secondaryDestination={flyoutResult.secondaryDestination}
            linkDestination={flyoutResult.linkDestination}
          />
        )}

        {isBanner && (
          <BannerDetailsContentText
            button={bannerResult?.button}
            headingText={bannerResult?.headingText}
            subheadingText={bannerResult?.subheadingText}
            backgroundColor={bannerResult?.backgroundColor}
            textareaColor={isHeroBanner(apiResult.type) ? heroResult?.textareaColor : null}
            secondaryText={
              isSecondaryBanner(apiResult.type) ? secondaryResult?.secondaryText : null
            }
          />
        )}
      </div>
    </Fragment>
  )
}
